/**
 * Schema.org generators for rich snippets in search results
 */

export interface FAQItem {
  question: string;
  answer: string;
}

export interface HowToStep {
  name: string;
  text: string;
}

export interface FinancialProductData {
  name: string;
  description: string;
  url: string;
  provider: string;
  updatedAt?: string;
  interestRate?: {
    min: number;
    max: number;
  };
  amount: {
    min: number;
    max: number;
  };
  featureList?: string[];
  aggregateRating?: {
    ratingValue: number;
    reviewCount: number;
  };
}

export interface ArticleData {
  title: string;
  description: string;
  image?: string;
  publishedAt: string;
  updatedAt?: string;
  author?: string;
  authorType?: "Person" | "Organization";
  authorRole?: string;
  /** Use "NewsArticle" for timely news pieces (better Google News / AIO). */
  type?: "Article" | "NewsArticle";
  url: string;
}

export interface ComparisonListItem {
  name: string;
  url: string;
  description?: string;
  provider?: string;
  amount?: string;
  interestRate?: string;
  term?: string;
  creditCheck?: string;
}

export interface ComparisonPageSchemaData {
  name: string;
  description: string;
  url: string;
  category: string;
  breadcrumbs: Array<{ name: string; url?: string }>;
  items: ComparisonListItem[];
  updatedAt?: string;
}

const toAbsoluteUrl = (url: string) => {
  if (url.startsWith("http")) return url;
  return `https://klarfinans.se${url.startsWith("/") ? url : `/${url}`}`;
};

/**
 * Generate FAQ schema for Q&A sections
 * Improves search visibility with rich FAQ snippets
 */
export const generateFAQSchema = (faqs: FAQItem[]) => {
  if (!faqs || faqs.length === 0) return null;

  return {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    "mainEntity": faqs.map(faq => ({
      "@type": "Question",
      "name": faq.question,
      "acceptedAnswer": {
        "@type": "Answer",
        "text": faq.answer
      }
    }))
  };
};

/**
 * Generate HowTo schema for step-by-step guides
 * Shows steps directly in search results
 */
export const generateHowToSchema = (title: string, steps: HowToStep[], description?: string) => {
  if (!steps || steps.length === 0) return null;

  return {
    "@context": "https://schema.org",
    "@type": "HowTo",
    "name": title,
    "description": description,
    "step": steps.map((step, i) => ({
      "@type": "HowToStep",
      "position": i + 1,
      "name": step.name,
      "text": step.text
    }))
  };
};

/**
 * Generate enhanced Article schema
 * Better indexing and rich article cards in search
 */
export const generateArticleSchema = (article: ArticleData) => {
  const author =
    article.author && article.authorType === "Person"
      ? {
          "@type": "Person",
          "name": article.author,
          ...(article.authorRole ? { "jobTitle": article.authorRole } : {}),
          "worksFor": { "@type": "Organization", "name": "Klarfinans", "url": "https://klarfinans.se" },
        }
      : {
          "@type": "Organization",
          "name": article.author || "KlarFinans",
          "url": "https://klarfinans.se",
        };

  return {
    "@context": "https://schema.org",
    "@type": article.type || "Article",
    "headline": article.title,
    "description": article.description,
    "image": article.image ? [article.image] : undefined,
    "datePublished": article.publishedAt,
    "dateModified": article.updatedAt || article.publishedAt,
    "author": author,
    "publisher": {
      "@type": "Organization",
      "name": "KlarFinans",
      "logo": {
        "@type": "ImageObject",
        "url": "https://klarfinans.se/logo.webp"
      }
    },
    "mainEntityOfPage": {
      "@type": "WebPage",
      "@id": article.url
    }
  };
};

/**
 * Generate BreadcrumbList schema
 * Shows breadcrumb navigation in search results
 * 
 * IMPORTANT: URLs should be absolute (starting with https://)
 * The last item should NOT have a URL (it represents the current page)
 */
export const generateBreadcrumbSchema = (items: Array<{ name: string; url?: string }>) => {
  const baseUrl = 'https://klarfinans.se';
  
  return {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    "itemListElement": items.map((item, index) => {
      const isLast = index === items.length - 1;
      // Convert relative URLs to absolute, or use the URL as-is if already absolute
      const fullUrl = item.url 
        ? (item.url.startsWith('http') ? item.url : `${baseUrl}${item.url}`)
        : undefined;
      
      const listItem: Record<string, unknown> = {
        "@type": "ListItem",
        "position": index + 1,
        "name": item.name
      };
      
      // Only include 'item' field if URL exists and it's NOT the last element
      // Google requires 'item' to be a valid URL, but the last item can omit it
      if (fullUrl && !isLast) {
        listItem.item = fullUrl;
      }
      
      return listItem;
    })
  };
};

/**
 * Generate CollectionPage and ItemList schema for comparison pages.
 * Helps search engines understand which lenders or products are compared.
 */
export const generateComparisonPageSchema = (page: ComparisonPageSchemaData) => {
  const itemListElement = page.items.map((item, index) => {
    const additionalProperty = [
      item.amount ? { "@type": "PropertyValue", "name": "Lånebelopp", "value": item.amount } : null,
      item.interestRate ? { "@type": "PropertyValue", "name": "Ränta", "value": item.interestRate } : null,
      item.term ? { "@type": "PropertyValue", "name": "Löptid", "value": item.term } : null,
      item.creditCheck ? { "@type": "PropertyValue", "name": "Kreditupplysning", "value": item.creditCheck } : null,
    ].filter(Boolean);

    return {
      "@type": "ListItem",
      "position": index + 1,
      "item": {
        "@type": "FinancialProduct",
        "name": item.name,
        "description": item.description,
        "url": toAbsoluteUrl(item.url),
        "category": page.category,
        "provider": {
          "@type": "Organization",
          "name": item.provider || item.name,
        },
        "additionalProperty": additionalProperty.length > 0 ? additionalProperty : undefined,
      },
    };
  });

  return [
    {
      "@context": "https://schema.org",
      "@type": "CollectionPage",
      "name": page.name,
      "description": page.description,
      "url": toAbsoluteUrl(page.url),
      "inLanguage": "sv-SE",
      "dateModified": page.updatedAt,
      "isPartOf": {
        "@type": "WebSite",
        "name": "Klarfinans",
        "url": "https://klarfinans.se",
      },
      "about": {
        "@type": "Thing",
        "name": page.category,
      },
      "mainEntity": {
        "@type": "ItemList",
        "name": `${page.category} som jämförs`,
        "numberOfItems": page.items.length,
        "itemListElement": itemListElement,
      },
    },
    generateBreadcrumbSchema(page.breadcrumbs),
  ];
};

/**
 * Extract FAQ items from HTML content
 * Looks for common FAQ patterns in content
 */
export const extractFAQFromContent = (html: string): FAQItem[] => {
  const faqs: FAQItem[] = [];
  const parser = new DOMParser();
  const doc = parser.parseFromString(html, 'text/html');

  // Look for FAQ sections (common patterns)
  const faqSections = doc.querySelectorAll('h2, h3');
  
  faqSections.forEach((heading) => {
    const text = heading.textContent || '';
    // If heading looks like a question
    if (text.includes('?') || text.toLowerCase().startsWith('vad') || 
        text.toLowerCase().startsWith('hur') || text.toLowerCase().startsWith('varför')) {
      
      const nextElement = heading.nextElementSibling;
      if (nextElement && (nextElement.tagName === 'P' || nextElement.tagName === 'DIV')) {
        faqs.push({
          question: text.trim(),
          answer: nextElement.textContent?.trim() || ''
        });
      }
    }
  });

  return faqs;
};

/**
 * Extract HowTo steps from HTML content
 * Looks for ordered lists or step patterns
 */
export const extractHowToFromContent = (html: string): HowToStep[] => {
  const steps: HowToStep[] = [];
  const parser = new DOMParser();
  const doc = parser.parseFromString(html, 'text/html');

  // Look for ordered lists
  const orderedLists = doc.querySelectorAll('ol');
  
  orderedLists.forEach((ol) => {
    const items = ol.querySelectorAll('li');
    items.forEach((li, index) => {
      const text = li.textContent?.trim();
      if (text) {
        steps.push({
          name: `Steg ${index + 1}`,
          text: text
        });
      }
    });
  });

  return steps;
};

/**
 * Generate FinancialProduct schema
 * Shows financial product information in rich snippets
 */
export const generateFinancialProductSchema = (product: FinancialProductData) => {
  const productUrl = toAbsoluteUrl(product.url);

  return {
    "@context": "https://schema.org",
    "@type": "FinancialProduct",
    "name": product.name,
    "description": product.description,
    "url": productUrl,
    "dateModified": product.updatedAt,
    "mainEntityOfPage": {
      "@type": "WebPage",
      "@id": productUrl,
      "dateModified": product.updatedAt
    },
    "provider": {
      "@type": "Organization",
      "name": product.provider
    },
    "interestRate": product.interestRate ? {
      "@type": "QuantitativeValue",
      "minValue": product.interestRate.min,
      "maxValue": product.interestRate.max,
      "unitText": "P1Y"
    } : undefined,
    "amount": {
      "@type": "MonetaryAmount",
      "minValue": product.amount.min,
      "maxValue": product.amount.max,
      "currency": "SEK"
    },
    "featureList": product.featureList,
    "aggregateRating": product.aggregateRating ? {
      "@type": "AggregateRating",
      "ratingValue": product.aggregateRating.ratingValue,
      "reviewCount": product.aggregateRating.reviewCount
    } : undefined
  };
};

/**
 * Generate Organization schema
 * Shows organization information in search results
 */
export const generateOrganizationSchema = (orgData: {
  name: string;
  address: string;
  telephone: string;
  email: string;
  url: string;
  logo?: string;
}) => {
  return {
    "@context": "https://schema.org",
    "@type": "FinancialService",
    "name": orgData.name,
    "address": {
      "@type": "PostalAddress",
      "streetAddress": orgData.address.split('\n')[0],
      "postalCode": orgData.address.split('\n')[1]?.split(' ')[0],
      "addressLocality": orgData.address.split('\n')[1]?.substring(7),
      "addressCountry": "SE"
    },
    "telephone": orgData.telephone,
    "email": orgData.email,
    "url": orgData.url,
    "logo": orgData.logo
  };
};
